import { Box, Chip, Stack } from "@mui/material";
import dayjs from "dayjs";
import { formatCurrency } from "../../services/valueFormatters.js";

const EMPTY_DATE = "0001-01-01";

const formatDate = (value) => {
  if (!value || value === EMPTY_DATE) return "";
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format("DD/MM/YYYY") : "";
};

const isValidDate = (value) => {
  if (!value || value === EMPTY_DATE) return false;
  return dayjs(value).isValid();
};

/** plantArrivalDate ?? today − portArrivalDate */
export const getLandTransitDays = (row) => {
  if (!isValidDate(row?.portArrivalDate)) return null;
  const plantDate = isValidDate(row?.plantArrivalDate)
    ? dayjs(row.plantArrivalDate)
    : dayjs();
  return plantDate.diff(dayjs(row.portArrivalDate), "day");
};

const setStatusColor = (statusCode) => {
  switch (statusCode) {
    case "EMBARCADO":
      return "default";
    case "EN_PUERTO":
      return "warning";
    case "EN_TRANSITO_LOCAL":
      return "info";
    case "ENTREGADO":
      return "success";
    default:
      return "default";
  }
};

const catalogueCode = (row, key) => row?.[key]?.code ?? "";
const catalogueName = (row, key) => row?.[key]?.name ?? "";

export const DEFAULT_VISIBLE_SHIPMENT_COLUMNS = [
  "ref",
  "portArrivalDate",
  "providerCode",
  "status",
  "plantArrivalDate",
];

export const createShipmentCrudColumns = () => [
  {
    field: "ref",
    headerName: "Ref",
    flex: 0.2,
    minWidth: 120,
  },
  {
    field: "portArrivalDate",
    headerName: "ETA",
    flex: 0.2,
    minWidth: 160,
    valueGetter: (_value, row) => formatDate(row.portArrivalDate),
  },
  {
    field: "providerCode",
    headerName: "Proveedor",
    flex: 0.2,
    minWidth: 120,
    valueGetter: (_value, row) => (row.provider ? row.provider.code : ""),
  },
  {
    field: "status",
    headerName: "Estatus",
    flex: 0.2,
    minWidth: 140,
    sortable: false,
    renderCell: (params) => (
      <Box display="flex" justifyContent="center" alignItems="center" width="100%">
        <Chip
          size="small"
          label={params.row.status?.name ?? ""}
          color={setStatusColor(params.row.status?.code)}
        />
      </Box>
    ),
  },
  {
    field: "plantArrivalDate",
    headerName: "Fecha llegada a planta",
    flex: 0.2,
    minWidth: 80,
    valueGetter: (_value, row) => formatDate(row.plantArrivalDate),
  },
  {
    field: "landTransitDays",
    headerName: "Días de transito terrestre",
    minWidth: 160,
    valueGetter: (_value, row) => getLandTransitDays(row),
  },
  {
    field: "remainingDays",
    headerName: "Días restantes",
    minWidth: 130,
    sortable: false,
    renderCell: (params) => {
      const remainingDays = -1 * dayjs().diff(params.row.portArrivalDate, "d");
      return (
        <Box display="flex" justifyContent="center" alignItems="center" width="100%">
          <Chip
            size="small"
            label={
              params.row.status?.code === "ENTREGADO" ? "-" : remainingDays
            }
            color={
              params.row.status?.code === "ENTREGADO"
                ? "default"
                : remainingDays > 5
                  ? "success"
                  : remainingDays > 0
                    ? "warning"
                    : "error"
            }
          />
        </Box>
      );
    },
  },
  {
    field: "tariffFractionName",
    headerName: "Material",
    minWidth: 140,
    valueGetter: (_value, row) => catalogueName(row, "tariffFraction"),
  },
  {
    field: "isFsc",
    headerName: "FSC",
    minWidth: 80,
    type: "boolean",
  },
  {
    field: "tariffFractionId",
    headerName: "Fracción Arancelaria",
    minWidth: 160,
    valueGetter: (_value, row) => {
      const tf = row.tariffFraction;
      return tf ? `${tf.code ?? ""} | ${tf.name ?? ""}`.trim() : "";
    },
  },
  {
    field: "igi",
    headerName: "IGI",
    minWidth: 90,
    valueFormatter: (value) =>
      value != null ? `${Number(value).toFixed(2)}%` : "",
  },
  {
    field: "totalNetWeight",
    headerName: "Peso Neto (kg)",
    minWidth: 180,
    type: "number",
  },
  {
    field: "cbm",
    headerName: "CBM",
    minWidth: 90,
    type: "number",
  },
  {
    field: "requestDate",
    headerName: "Fecha de pedido",
    minWidth: 140,
    valueGetter: (_value, row) => formatDate(row.requestDate),
  },
  {
    field: "deliveryDate",
    headerName: "Fecha de Entrega",
    minWidth: 140,
    valueGetter: (_value, row) => formatDate(row.deliveryDate),
  },
  {
    field: "departureDate",
    headerName: "Fecha de Zarpe",
    minWidth: 140,
    valueGetter: (_value, row) => formatDate(row.departureDate),
  },
  {
    field: "shipmentTransportTypeId",
    headerName: "Tipo de Envío",
    minWidth: 140,
    valueGetter: (_value, row) => catalogueCode(row, "shipmentTransportType"),
  },
  {
    field: "portDestinationId",
    headerName: "Puerto de llegada",
    minWidth: 150,
    valueGetter: (_value, row) => catalogueCode(row, "portDestination"),
  },
  {
    field: "foreignTransportId",
    headerName: "Transporte Extranjero",
    minWidth: 160,
    valueGetter: (_value, row) => catalogueCode(row, "foreignTransport"),
  },
  {
    field: "masterBillofLanding",
    headerName: "MBL",
    minWidth: 180,
  },
  {
    field: "hasForwarder",
    headerName: "Tiene Forwarder",
    minWidth: 130,
    type: "boolean",
  },
  {
    field: "forwarderId",
    headerName: "Forwarder",
    minWidth: 120,
    valueGetter: (_value, row) => catalogueCode(row, "forwarder"),
  },
  {
    field: "forwarderGuideNo",
    headerName: "No. Guía Forwarder",
    minWidth: 150,
  },
  {
    field: "portOfOriginId",
    headerName: "Puerto de Origen",
    minWidth: 140,
    valueGetter: (_value, row) => catalogueCode(row, "portOfOrigin"),
  },
  {
    field: "oceanFreightCost",
    headerName: "Costo Flete Marítimo (USD)",
    minWidth: 180,
    valueFormatter: (value) =>
      value != null ? formatCurrency(value, "MXN") : "",
  },
  {
    field: "numContainers",
    headerName: "No. Contenedores",
    minWidth: 170,
    type: "number",
  },
  {
    field: "containers",
    headerName: "Contenedores",
    minWidth: 160,
    sortable: false,
    renderCell: (params) => {
      if (!params.row.containers?.length) return "";
      return (
        <Stack direction="column" spacing={0.5}>
          {params.row.containers.map((container) => (
            <Chip size="small" label={container.code} key={container.id} />
          ))}
        </Stack>
      );
    },
  },
  {
    field: "customsAgentId",
    headerName: "A.A.",
    minWidth: 140,
    valueGetter: (_value, row) => catalogueCode(row, "customsAgent"),
  },
  {
    field: "patent",
    headerName: "Patente",
    minWidth: 110,
  },
  {
    field: "customsAgentCost",
    headerName: "Costo Agente Aduanal",
    minWidth: 160,
    valueFormatter: (value) =>
      value != null ? formatCurrency(value, "MXN") : "",
  },
  {
    field: "requestNo",
    headerName: "No. Pedimento",
    minWidth: 130,
  },
  {
    field: "invoiceNo",
    headerName: "No. Factura",
    minWidth: 120,
  },
  {
    field: "shipmentTypeId",
    headerName: "Tipo de Embarque",
    minWidth: 150,
    valueGetter: (_value, row) => catalogueName(row, "shipmentType"),
  },
  {
    field: "shipmentModeId",
    headerName: "Modo de Embarque",
    minWidth: 150,
    valueGetter: (_value, row) => catalogueName(row, "shipmentMode"),
  },
  {
    field: "forwarderLandId",
    headerName: "Forwarder Terrestre",
    minWidth: 160,
    valueGetter: (_value, row) => catalogueCode(row, "forwarderLand"),
  },
  {
    field: "forwarderLandCost",
    headerName: "Costo Flete",
    minWidth: 180,
    valueFormatter: (value) =>
      value != null ? formatCurrency(value, "MXN") : "",
  },
  {
    field: "landCostByContainer",
    headerName: "$ Flete/cntr",
    minWidth: 240,
    valueFormatter: (value) =>
      value != null ? formatCurrency(value, "MXN") : "",
  },
  {
    field: "freeDays",
    headerName: "Días Libres",
    minWidth: 110,
    type: "number",
  },
  {
    field: "originPlantTransitDays",
    headerName: "Días de Tránsito Origen a Planta",
    minWidth: 110,
    type: "number",
  },
  {
    field: "portPlantTransitDays",
    headerName: "Días de Tránsito Puerto a Planta",
    minWidth: 110,
    type: "number",
  },
  {
    field: "hasStorage",
    headerName: "Almacenaje",
    minWidth: 140,
    type: "boolean",
  },
  {
    field: "storageCost",
    headerName: "Costo de Almacenaje",
    minWidth: 150,
    valueFormatter: (value) =>
      value != null ? formatCurrency(value, "MXN") : "",
  },
  {
    field: "delayCharges",
    headerName: "Cargos por Demora",
    minWidth: 150,
    valueFormatter: (value) =>
      value != null ? formatCurrency(value, "MXN") : "",
  },
  {
    field: "observations",
    headerName: "Observaciones",
    minWidth: 200,
    flex: 0.3,
  },
];

export const buildShipmentColumnVisibilityModel = (columns) =>
  columns.reduce((acc, column) => {
    acc[column.field] = DEFAULT_VISIBLE_SHIPMENT_COLUMNS.includes(column.field);
    return acc;
  }, {});
